import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import colors from '../ColorConfig/colorconfig';

// Subcomponents
import SectionHeader from './RecentWorkSub/SecHeader';
import MobileWorksView from './RecentWorkSub/MobileView';
import DesktopWorksGrid from './RecentWorkSub/DesktopView';
import LoadingCard from './RecentWorkSub/Loading';

const RecentWorks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [worksData, setWorksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch industries data from API
  useEffect(() => {
    const fetchIndustriesData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Industries_IndustriesList');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Transform API data to match component structure
        const transformedData = data.map(item => ({
          id: item.id,
          title: item.title,
          description: item.description,
          image: item.image.startsWith('http') 
            ? item.image 
            : `https://api.dremerz.net/uploads/${item.image}`,
          category: item.category,
          datetime: item.datetime
        }));
        
        setWorksData(transformedData);
        setError(null);
      } catch (err) {
        console.error('Error fetching industries data:', err);
        setError('Failed to load industries data');       
      } finally {
        setLoading(false);
      }
    };

    fetchIndustriesData();
  }, []);

  // Handle scroll to update indicators
  const handleScroll = () => {
    if (!scrollContainerRef.current || worksData.length === 0) return;

    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.children[0]?.offsetWidth + 16;
    const containerWidth = container.offsetWidth;
    
    // Calculate which card is centered in the view
    const scrollCenter = scrollLeft + (containerWidth / 2);
    const activeIndex = Math.floor(scrollCenter / cardWidth);
    
    if (activeIndex >= 0 && activeIndex < worksData.length && activeIndex !== currentSlide) {
      setCurrentSlide(activeIndex);
    }
  };

  // Scroll to services section if navigated from header
  useEffect(() => {
    if (location.state && location.state.scrollToServices) {
      setTimeout(() => {
        const element = document.getElementById('services');
        if (element) {
          const headerHeight = document.querySelector('.navbar-custom').offsetHeight;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { 
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const section = document.querySelector('.works-container');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  // Text animation observer
  useEffect(() => {
    const textObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('text-visible');
          }
        });
      },
      { threshold: 0.3 }
    );

    if (titleRef.current) {
      textObserver.observe(titleRef.current);
    }
    if (subtitleRef.current) {
      textObserver.observe(subtitleRef.current);
    }

    return () => {
      if (titleRef.current) {
        textObserver.unobserve(titleRef.current);
      }
      if (subtitleRef.current) {
        textObserver.unobserve(subtitleRef.current);
      }
    };
  }, []);

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.5, delay: 0.8 }
    },
    hover: {
      scale: 1.05,
      transition: { duration: 0.3 }
    }
  };

  const handleViewAllServices = () => {
    navigate('/services', { 
      state: { selectedServiceId: 'all' } 
    });
  };

  return (
    <>
      <style>{`
        ${styles}
      `}</style>

      <div className="works-container">
        <div className="container">
          {/* Section Header */}
          <SectionHeader 
            titleRef={titleRef}
            subtitleRef={subtitleRef}
            colors={colors}
          />

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <p className="mb-0">{error}. Showing fallback data.</p>
            </div>
          )}

          {/* Mobile Horizontal Scroll View */}
          {isMobile && (
            <MobileWorksView
              worksData={worksData}
              loading={loading}
              currentSlide={currentSlide}
              scrollContainerRef={scrollContainerRef}
              setCurrentSlide={setCurrentSlide}
              onScroll={handleScroll}
            />
          )}

          {/* Desktop Grid View */}
          {!isMobile && (
            <DesktopWorksGrid
              worksData={worksData}
              loading={loading}
              isVisible={isVisible}
            />
          )}

          {/* View All Button */}
          <motion.div 
            className="row mt-2"
            variants={buttonVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover="hover"
          >
            <div className="col-12 text-center">
              {/* Button commented out as per original code */}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

// Styles moved to separate constant
const styles = `
  .works-container {
    position: relative;
    background: ${colors.bodyBlack};
    overflow: hidden;
    padding: 1rem 0;
  }
  
  /* New Advanced Sliding Animations */
  @keyframes slideInBounceLeft {
    0% {
      transform: translateX(-120px) translateY(30px) scale(0.8);
      opacity: 0;
    }
    60% {
      transform: translateX(15px) translateY(-5px) scale(1.02);
      opacity: 0.8;
    }
    100% {
      transform: translateX(0) translateY(0) scale(1);
      opacity: 1;
    }
  }

  @keyframes slideInBounceRight {
    0% {
      transform: translateX(120px) translateY(30px) scale(0.8);
      opacity: 0;
    }
    60% {
      transform: translateX(-15px) translateY(-5px) scale(1.02);
      opacity: 0.8;
    }
    100% {
      transform: translateX(0) translateY(0) scale(1);
      opacity: 1;
    }
  }

  @keyframes slideInBounceCenter {
    0% {
      transform: translateY(80px) scale(0.7);
      opacity: 0;
    }
    50% {
      transform: translateY(-10px) scale(1.05);
      opacity: 0.7;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  @keyframes fadeInUp {
    0% {
      transform: translateY(50px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* Enhanced Text Animations */
  @keyframes titleSlideIn {
    0% {
      transform: translateY(60px) scale(0.9);
      opacity: 0;
      filter: blur(4px);
    }
    60% {
      transform: translateY(-8px) scale(1.02);
      opacity: 0.9;
      filter: blur(1px);
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 1;
      filter: blur(0);
    }
  }

  @keyframes subtitleSlideIn {
    0% {
      transform: translateY(40px) scale(0.95);
      opacity: 0;
      filter: blur(4px);
    }
    70% {
      transform: translateY(-5px) scale(1.01);
      opacity: 0.8;
      filter: blur(1px);
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 1;
      filter: blur(0);
    }
  }

  /* Animation Classes */
  .animate-text {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .animate-text.visible {
    opacity: 1;
    transform: translateY(0);
    animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  .animate-card {
    opacity: 0;
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .animate-card.visible.slide-left {
    animation: slideInBounceLeft 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  .animate-card.visible.slide-right {
    animation: slideInBounceRight 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  .animate-card.visible.slide-center {
    animation: slideInBounceCenter 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  /* Text Animation Classes */
  .animated-title {
    opacity: 0;
    transform: translateY(60px) scale(0.9);
    filter: blur(2px);
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .animated-subtitle {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
    filter: blur(2px);
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .text-visible {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
  
  .animated-title.text-visible {
    animation: titleSlideIn 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .animated-subtitle.text-visible {
    animation: subtitleSlideIn 1.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    animation-delay: 0.2s;
  }
  
  /* Enhanced Work Card Styles */
  .work-card {
    background: ${colors.cardBgBlack};
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border-bottom-left-radius: 52px;
    border-top-right-radius: 52px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  
  .work-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(229, 118, 39, 0.08), 
      transparent
    );
    transition: left 0.6s ease;
    z-index: 1;
  }
  
  .work-card:hover::before {
    left: 100%;
  }
  
  .work-card:hover {
    transform: translateY(-8px);
    border-color: var(--color-primary);
    background: ${colors.hoverBlack};
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.4),
      0 0 0 1px var(--color-primary);
  }
  
  /* Image Container */
  .work-image-container {
    position: relative;
    overflow: hidden;
    border-radius: 12px 12px 0 0;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    height: 180px;
  }
  
  .work-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.5s ease;
    filter: brightness(0.9);
  }
  
  .industry-title {
    // fontSize: '2.5rem';
  }
  
  .industry-subtitle {
    fontSize: '1.1rem';
    opacity: '0.8';
  }
  
  .work-card:hover .work-image {
    transform: scale(1.08);
    filter: brightness(1.1);
  }
  
  /* Category Badge */
  .category-badge1 {
    position: absolute;
    top: 12px;
    left: 12px;
    background: var(--color-primary);
    backdrop-filter: blur(10px);
    color: var(--color-white);
    padding: 8px 12px;
    border-radius: 15px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 2;
    border-color: var(--color-primary);
  }

  .category-badge1:hover {
    background: var(--color-primary);
    color: var(--color-white);
  }
  
  /* Content Spacing */
  .card-content {
    position: relative;
    z-index: 2;
    padding: 0.5rem;
  }
  
  .card-title {
    color: ${colors.white};
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 0.8rem;
  }
  
  .card-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 1rem;
    min-height: 63px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  /* Error message styles */
  .error-message {
    color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
    border: 1px solid rgba(255, 107, 107, 0.2);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
    text-align: center;
  }

  /* Placeholder styles */
  .placeholder {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    display: inline-block;
    height: 1em;
  }

  .placeholder-glow .placeholder {
    animation: placeholder-glow 2s ease-in-out infinite alternate;
  }

  @keyframes placeholder-glow {
    50% {
      opacity: 0.5;
    }
  }

  /* Mobile Horizontal Scroll Styles */
  @media (max-width: 768px) {
    .works-container {
      padding-bottom: 0.5rem;
    }
    
    .industry-title {
      font-size: 1.5rem;
    }
    
    .industry-subtitle {
      font-size: 1rem;
    }

    .mobile-scroll-container {
      position: relative;
      overflow: hidden;
      margin: 0 -15px;
      padding-bottom: 0.5rem;
    }

    .mobile-works-wrapper {
      display: flex;
      overflow-x: auto;
      scroll-behavior: smooth;
      gap: 16px;
      padding: 0 20px 10px;
      scrollbar-width: none;
      -ms-overflow-style: none;
      cursor: grab;
    }

    .mobile-works-wrapper:active {
      cursor: grabbing;
    }

    .mobile-works-wrapper::-webkit-scrollbar {
      display: none;
    }

    .mobile-work-card {
      flex: 0 0 280px;
      height: auto;
      scroll-snap-align: start;
    }

    .mobile-work-card .work-card {
      width: 100%;
      margin: 0;
      transform: translateY(0);
    }

    .mobile-work-card .work-card:hover {
      transform: translateY(-5px);
    }

    /* Slide Indicators */
    .slide-indicators {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin-top: 10px;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    .slide-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.3);
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .slide-indicator.active {
      background: var(--color-primary);
      transform: scale(1.3);
    }

    /* Hide desktop grid on mobile */
    .desktop-grid {
      display: none;
    }
  }

  /* Desktop styles */
  @media (min-width: 769px) {
    .mobile-scroll-container {
      display: none;
    }
  }

  @media (max-width: 480px) {
    .industry-title {
      font-size: 1.5rem;
    }
    
    .industry-subtitle {
      font-size: 1rem;
    }

    .mobile-work-card {
      flex: 0 0 280px;
      padding-top: 10px
    }

    .mobile-scroll-container {
      padding-bottom: 0.3rem;
    }
  }
`;

export default RecentWorks;